import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export const usePermissions = () => {
  const { user, hasPermission, hasAnyPermission } = useAuth();

  const getModulePermissions = useCallback((module: string) => {
    if (!user) {
      return {
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false,
      };
    }

    if (user.is_superadmin) {
      return {
        canCreate: true,
        canRead: true,
        canUpdate: true,
        canDelete: true,
      };
    }

    const permission = user.permissions?.find(p => p.module_name === module);
    if (!permission) {
      return {
        canCreate: false,
        canRead: false,
        canUpdate: false,
        canDelete: false,
      };
    }

    return {
      canCreate: permission.can_create,
      canRead: permission.can_read,
      canUpdate: permission.can_update,
      canDelete: permission.can_delete,
    };
  }, [user]);

  const getAllUserPermissions = useCallback(() => {
    if (!user) return {};

    if (user.is_superadmin) {
      // Return all permissions for superadmin
      const modules = [
        'users', 'roles', 'therapists', 'students', 'schedules',
        'assignments', 'fees', 'payments', 'document-transmittals',
        'pending-reports', 'settings'
      ];
      
      const allPermissions: Record<string, any> = {};
      modules.forEach(module => {
        allPermissions[module] = {
          canCreate: true,
          canRead: true,
          canUpdate: true,
          canDelete: true,
        };
      });
      return allPermissions;
    }

    const allPermissions: Record<string, any> = {};
    user.permissions?.forEach(permission => {
      allPermissions[permission.module_name] = {
        canCreate: permission.can_create,
        canRead: permission.can_read,
        canUpdate: permission.can_update,
        canDelete: permission.can_delete,
      };
    });

    return allPermissions;
  }, [user]);

  const canAccessModule = useCallback((module: string): boolean => {
    return hasAnyPermission(module);
  }, [hasAnyPermission]);

  const canPerformAction = useCallback((module: string, action: string): boolean => {
    return hasPermission(module, action);
  }, [hasPermission]);

  return {
    user,
    hasPermission,
    hasAnyPermission,
    getModulePermissions,
    getAllUserPermissions,
    canAccessModule,
    canPerformAction,
  };
};